import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

/** One priced item on a quote. `lineKey` is the server's identity for it. */
export interface QuoteLine {
  lineKey: string;
  monthlyAmount: number;
  description: string | null;
}

export interface Quote {
  id: number;
  leadId: number;
  customerId: number | null;
  /** Bumped by the server on every revision, so two people editing one quote
   *  cannot silently overwrite each other. Printed, because "v3" is how a rep
   *  and a customer talk about which paper they are looking at. */
  version: number;
  status: QuoteStatus;
  monthlyTotal: number | null;
  profitScenarioId: number | null;
  termsVersion: string | null;
  validUntil: string | null;
  acceptedAt: string | null;
  /** Set when the quote was accepted: accepting is what MAKES the contract. */
  acceptedContractId: number | null;
  lines: QuoteLine[];
  createdAt: string;
  updatedAt: string;

  // What was quoted, read back. Accepting defaults from these rather than
  // asking a second time for what the quote already said.
  title: string | null;
  terms: string | null;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  billingMethod: string | null;
  billingDayOfMonth: number | null;
  expectedMonthlyCount: number | null;
  poReleaseNumber: string | null;
  transactionCodeId: number | null;
}

export type QuoteStatus =
  | 'Draft' | 'InternalReview' | 'Approved' | 'Sent'
  | 'Viewed' | 'Accepted' | 'Rejected' | 'Expired';

/**
 * Where a quote may go from where it is, mirroring `QuoteLogic.Transitions`.
 *
 * Two of the server's moves are NOT here, and their absence is the point:
 * `Approved` is reached only by POST /approve and `Accepted` only by
 * POST /accept. Both take arguments and both do more than change a word -
 * approving is a decision under its own permission, and accepting writes a
 * contract. Listing them as ordinary transitions would offer a button that
 * the transition endpoint refuses.
 */
export const QUOTE_TRANSITIONS: Record<QuoteStatus, QuoteStatus[]> = {
  Draft: ['InternalReview', 'Expired'],
  InternalReview: ['Draft', 'Expired'],
  Approved: ['Sent', 'Expired'],
  Sent: ['Viewed', 'Rejected', 'Expired'],
  Viewed: ['Rejected', 'Expired'],
  Accepted: [],
  Rejected: [],
  Expired: [],
};

/** Nothing moves out of these; the quote's life is over. */
export const QUOTE_TERMINAL: QuoteStatus[] = ['Accepted', 'Rejected', 'Expired'];

/** What to print for a status. The server's words are identifiers - a rep
 *  reads "Internal review", not `InternalReview`. */
export const QUOTE_STATUS_LABELS: Record<QuoteStatus, string> = {
  Draft: 'Draft',
  InternalReview: 'Internal review',
  Approved: 'Approved',
  Sent: 'Sent',
  Viewed: 'Viewed by the customer',
  Accepted: 'Accepted',
  Rejected: 'Rejected',
  Expired: 'Expired',
};

export interface CreateQuoteLine {
  lineKey: string;
  monthlyAmount: number;
  description: string | null;
}

export interface CreateQuote {
  leadId: number;
  lines: CreateQuoteLine[];
  termsVersion: string | null;
  validUntil: string | null;
  /** What is being quoted, in the terms a contract states them. All optional:
   *  a quote raised as a bare price list is still a quote. */
  title: string | null;
  terms: string | null;
  effectiveFrom: string | null;
  effectiveTo: string | null;
  billingMethod: string | null;
  billingDayOfMonth: number | null;
  expectedMonthlyCount: number | null;
  poReleaseNumber: string | null;
  transactionCodeId: number | null;
}

export interface AcceptQuote {
  transactionCodeId: number;
  effectiveFrom: string;
  effectiveTo: string;
}

/** A transaction code, as `/api/transaction-codes` returns one. Accepting a
 *  quote needs one because the contract it writes is billed under it. */
export interface TransactionCode {
  id: number;
  shortDescription: string;
  defaultAmount: number;
}

/**
 * The quote commands, which are the lead's paperwork.
 *
 * A contract cannot be raised against a lead: `POST /api/contracts` requires a
 * customer, and a lead has none until one is signed - the customer record is
 * created inside the signing transaction. What a lead has instead is a quote,
 * and accepting one is what produces the contract and the account together.
 *
 * The API for all of that already existed and had no screen at any point.
 */
@Injectable({ providedIn: 'root' })
export class QuoteApiService {
  constructor(private readonly http: HttpClient) {}

  forLead(leadId: number): Observable<Quote[]> {
    return this.http.get<Quote[]>(`/api/quotes/lead/${leadId}`);
  }

  create(request: CreateQuote): Observable<Quote> {
    return this.http.post<Quote>('/api/quotes', request);
  }

  /** Move it along its own state machine. `Approved` and `Accepted` are not
   *  reachable here - see QUOTE_TRANSITIONS. */
  transition(id: number, to: QuoteStatus): Observable<Quote> {
    return this.http.post<Quote>(`/api/quotes/${id}/transition`, { to });
  }

  /** The internal decision, under `Automation.Approve` - a different
   *  permission from working the quote, because it is a different act. */
  approve(id: number, approve: boolean, reason: string | null): Observable<Quote> {
    return this.http.post<Quote>(`/api/quotes/${id}/approve`, { approve, reason });
  }

  /** The customer said yes. This writes the contract, and creates the customer
   *  account if the lead did not have one. */
  accept(id: number, request: AcceptQuote): Observable<Quote> {
    return this.http.post<Quote>(`/api/quotes/${id}/accept`, request);
  }

  transactionCodes(): Observable<TransactionCode[]> {
    return this.http.get<TransactionCode[]>('/api/transaction-codes');
  }
}
